import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Button, Input, Space, Divider, Typography, Modal, notification,
} from 'antd';
import locale from 'antd/es/locale/ru_RU';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import TableConfig from './TableConfig';
import NewAnswerForm from './NewAnswerForm';
import {
  describeChains,
  replaceAnswersWithChains,
  sortByPosition,
  updateAnswersInPlace,
} from '../api/answers';
import { showServerError } from '../api/errors';

const { Text } = Typography;

const SortableItem = SortableElement((props) => (
  <tr {...props} />
));
const SortableBody = SortableContainer((props) => (
  <tbody {...props} />
));

function Answers(props) {
  const { data: questionData, getData, onOpenChain } = props;
  const { answers, id: questionId } = questionData;

  const [edditableRow, setEdditableRow] = useState('');
  const [searchText, setSearchText] = useState();
  const [searchedColumn, setSearchedColumn] = useState();
  const input = useRef(null);

  const [dataSource, setDataSource] = useState([]);

  // Дерево анкеты перезагружается после каждой правки, поэтому таблица должна
  // следовать за props, а не инициализироваться один раз при монтировании.
  useEffect(() => {
    setDataSource(sortByPosition(answers || []).map((el, index) => ({ ...el, position: index })));
  }, [answers]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex, searchItemText) => ({
    // eslint-disable-next-line react/no-unstable-nested-components
    filterDropdown: ({
      setSelectedKeys, selectedKeys, confirm, clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={input}
          placeholder={`Найти ${searchItemText}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Поиск
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Очистить
          </Button>
        </Space>
      </div>
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => (record[dataIndex]
      ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
      : ''),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => input.current.select(), 100);
      }
    },
    render: (text) => (searchedColumn === dataIndex ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text ? text.toString() : ''}
      />
    ) : (
      text
    )),
  });

  const edditCansel = () => {
    setEdditableRow('');
  };

  const edditRow = (row) => {
    setEdditableRow(row.id);
  };

  const refresh = async () => {
    if (getData) await getData();
  };

  // Добавление и удаление ответа возможны только полной заменой, а она
  // пересоздаёт id и молча сносит все привязки доп. вопросов этого вопроса.
  // Поэтому предупреждаем заранее и восстанавливаем привязки сами.
  const applyReplace = async (nextAnswers) => {
    try {
      const { failed } = await replaceAnswersWithChains(questionId, nextAnswers);
      if (failed.length) {
        notification.warning({
          message: 'Ответы сохранены, но часть связей не восстановилась',
          description: failed
            .map((f) => `«${f.answertxt}» → «${f.questiontext}»`)
            .join('; '),
          duration: 0,
        });
      } else {
        notification.success({ message: 'Выполнено' });
      }
      await refresh();
    } catch (e) {
      showServerError(e, 'Не удалось сохранить ответы');
      await refresh();
    }
  };

  const confirmReplace = (title, nextAnswers) => {
    const affected = describeChains(dataSource);

    if (!affected.length) {
      applyReplace(nextAnswers);
      return;
    }

    Modal.confirm({
      title,
      width: 620,
      content: (
        <>
          <p>
            Ответы этого вопроса будут пересозданы с новыми идентификаторами.
            Привязки дополнительных вопросов будут восстановлены автоматически:
          </p>
          <ul>
            {affected.map((item) => (
              <li key={item.answertxt}>
                {`«${item.answertxt}» → ${item.questions.map((q) => `«${q}»`).join(', ')}`}
              </li>
            ))}
          </ul>
          <Text type="secondary">
            Если какая-то связь не восстановится, я покажу её отдельным сообщением.
          </Text>
        </>
      ),
      okText: 'Продолжить',
      cancelText: 'Отмена',
      onOk: () => applyReplace(nextAnswers),
    });
  };

  const addAnswer = (answertxt, weight) => {
    confirmReplace('Добавить ответ?', [
      ...dataSource,
      {
        type: 3, answertxt, weight, additional_questions: [],
      },
    ]);
  };

  const deleteAnswer = (id) => {
    const removed = dataSource.find((a) => a.id === id);
    confirmReplace(
      `Удалить ответ «${removed?.answertxt || ''}»?`,
      dataSource.filter((a) => a.id !== id),
    );
  };

  // Точечно шлём только изменённый ответ: если в вопросе есть уже
  // использованные в анкетировании ответы, пакетный запрос отклоняется целиком.
  const edditAnswer = async (textValue, weightValue, answerId) => {
    const target = dataSource.find((a) => a.id === answerId);
    if (!target) return;

    try {
      await updateAnswersInPlace(questionId, [
        { ...target, answertxt: textValue, weight: weightValue },
      ]);
      setEdditableRow('');
      notification.success({ message: 'Ответ сохранён' });
      await refresh();
    } catch (e) {
      showServerError(e, 'Не удалось изменить ответ');
    }
  };

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;

    const reordered = arrayMoveImmutable(dataSource.slice(), oldIndex, newIndex)
      .filter((el) => !!el)
      .map((el, index) => ({ ...el, position: index }));

    setDataSource(reordered);

    try {
      await updateAnswersInPlace(questionId, reordered);
      await refresh();
    } catch (e) {
      showServerError(e, 'Не удалось изменить порядок ответов');
      // Порядок на сервере не поменялся — возвращаем таблицу к исходному виду.
      setDataSource(sortByPosition(answers || []).map((el, index) => ({ ...el, position: index })));
    }
  };

  // eslint-disable-next-line
  function DraggableContainer(props) {
    return (
      <SortableBody
        useDragHandle
        disableAutoscroll
        helperClass="row-dragging"
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  }

  // eslint-disable-next-line
  function DraggableBodyRow({ className, style, ...restProps }) {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource.findIndex((x) => x.position === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  }

  const openChain = (row) => {
    if (onOpenChain) onOpenChain(questionData, row);
  };

  const columns = TableConfig(
    getColumnSearchProps,
    edditRow,
    edditableRow,
    edditCansel,
    edditAnswer,
    deleteAnswer,
    openChain,
  );

  return (
    <>
      <NewAnswerForm onAdd={addAnswer} />

      <Table
        style={{ marginTop: '28px' }}
        rowKey="position"
        dataSource={dataSource}
        columns={columns.columns}
        size="middle"
        bordered
        locale={locale.Table}
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
        pagination={false}
      />
      <Divider />
      <Text type="secondary" style={{ marginBottom: '10px' }}>
        ** Обратите внимание, что порядок сортировки в этой таблице,
        влияет на отображение данных при дальнейшем использовании
      </Text>
    </>
  );
}

export default Answers;
